import type { Skill } from '../storage/types.js';

export type TileCount = 2 | 3 | 4;

/** Tile count grows with level: L1 → 2, L2 → 3, L3+ → 4. */
export function tileCountForLevel(level: number): TileCount {
  if (level <= 1) return 2;
  if (level === 2) return 3;
  return 4;
}

export interface RecognitionLevelParams {
  digits: readonly number[];
  tileCount: TileCount;
}
export interface CountingLevelParams {
  minN: number;
  maxN: number;
  tileCount: TileCount;
}
export interface AdditionLevelParams {
  sumCap: number;
  allowOperandZero: boolean;
  tileCount: TileCount;
}
export interface SubtractionLevelParams {
  aMax: number;
  tileCount: TileCount;
}

export const MAX_LEVEL: Record<Skill, number> = {
  recognition: 4,
  counting: 4,
  addition: 4,
  subtraction: 4,
};

const RECOGNITION_LEVEL_DIGITS: ReadonlyArray<readonly number[]> = [
  [1, 2, 3],
  [0, 1, 2, 3, 4, 5],
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
];

export function recognitionParams(level: number): RecognitionLevelParams {
  const idx = clampIndex(level, RECOGNITION_LEVEL_DIGITS.length);
  return {
    digits: RECOGNITION_LEVEL_DIGITS[idx]!,
    tileCount: tileCountForLevel(level),
  };
}

const COUNTING_RANGES: ReadonlyArray<readonly [number, number]> = [
  [1, 3],
  [1, 5],
  [1, 10],
  [1, 20],
];

export function countingParams(level: number): CountingLevelParams {
  const idx = clampIndex(level, COUNTING_RANGES.length);
  const range = COUNTING_RANGES[idx]!;
  return {
    minN: range[0],
    maxN: range[1],
    tileCount: tileCountForLevel(level),
  };
}

const ADDITION_LEVELS: ReadonlyArray<{ sumCap: number; allowOperandZero: boolean }> = [
  { sumCap: 4, allowOperandZero: false },
  { sumCap: 10, allowOperandZero: false },
  { sumCap: 15, allowOperandZero: true },
  { sumCap: 20, allowOperandZero: true },
];

export function additionParams(level: number): AdditionLevelParams {
  const idx = clampIndex(level, ADDITION_LEVELS.length);
  const def = ADDITION_LEVELS[idx]!;
  return {
    sumCap: def.sumCap,
    allowOperandZero: def.allowOperandZero,
    tileCount: tileCountForLevel(level),
  };
}

const SUBTRACTION_A_MAX: readonly number[] = [3, 5, 10, 20];

export function subtractionParams(level: number): SubtractionLevelParams {
  const idx = clampIndex(level, SUBTRACTION_A_MAX.length);
  return {
    aMax: SUBTRACTION_A_MAX[idx]!,
    tileCount: tileCountForLevel(level),
  };
}

function clampIndex(level: number, length: number): number {
  if (level < 1) return 0;
  if (level > length) return length - 1;
  return level - 1;
}
